import { MapPin, Phone, Clock, Mail, MessageCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function Contact() {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    reason: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Format the email body
    const subject = language === 'en' 
      ? `Appointment Request from ${formData.fullName}`
      : `Demande de Rendez-vous de ${formData.fullName}`;
    
    const body = language === 'en'
      ? `Name: ${formData.fullName}%0D%0APhone: ${formData.phone}%0D%0AEmail: ${formData.email || 'Not provided'}%0D%0APreferred Date: ${formData.date}%0D%0APreferred Time: ${formData.time}%0D%0AReason for Visit: ${formData.reason || 'Not specified'}`
      : `Nom: ${formData.fullName}%0D%0ATéléphone: ${formData.phone}%0D%0AEmail: ${formData.email || 'Non fourni'}%0D%0ADate Préférée: ${formData.date}%0D%0AHeure Préférée: ${formData.time}%0D%0AMotif de la Visite: ${formData.reason || 'Non spécifié'}`;
    
 
    const mailtoLink = `mailto:cliniquemainsdoucesplus@gmail.com?subject=${encodeURIComponent(subject)}&body=${body}`;
  
    window.location.href = mailtoLink;
    
    alert(language === 'en' 
      ? 'Your email client will open to send the appointment request.'
      : 'Votre client de messagerie va s\'ouvrir pour envoyer la demande de rendez-vous.');
  };

  return (
    <section className="py-16 sm:py-20 md:py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <span className="inline-block bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            {t('Get in Touch', 'Contactez-Nous')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-blue-900 mb-4">
            {t('Contact Us', 'Contactez-Nous')}
          </h2>
          <p className="text-base sm:text-lg text-gray-600">
            {t(
              'We are here to answer all your questions and take care of your health',
              'Nous sommes là pour répondre à toutes vos questions et prendre soin de votre santé'
            )}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left Column - Contact Info */}
          <div>
            <div className="bg-gradient-to-br from-blue-50 to-amber-50 rounded-3xl p-6 sm:p-8 mb-6">
              <h3 className="text-xl font-bold text-blue-900 mb-6">
                {t('Contact Information', 'Informations de Contact')}
              </h3>

              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-white p-4 rounded-xl">
                  <div className="bg-blue-600 p-3 rounded-lg flex-shrink-0">
                    <MapPin className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">
                      {t('Address', 'Adresse')}
                    </h4>
                    <p className="text-gray-700">
                      Tradex Simbock<br />
                      {t('Yaoundé, Cameroon', 'Yaoundé, Cameroun')}
                    </p>
                    <p className="text-sm text-amber-600 mt-1">
                      {t('Opposite the small market', 'En face du marché ')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white p-4 rounded-xl">
                  <div className="bg-blue-600 p-3 rounded-lg flex-shrink-0">
                    <Phone className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">
                      {t('Phone', 'Téléphone')}
                    </h4>
                    <a href="tel:690640369" className="text-blue-600 hover:text-blue-800 font-medium text-lg block">
                      +237 690 640 369
                    </a>
                    <a href="tel:653474614" className="text-blue-600 hover:text-blue-800 font-medium">
                      +237 653 474 614
                    </a>
                    <p className="text-sm text-gray-500 mt-1">
                      {t('Available 24/7', 'Disponible 24h/24')}
                    </p>
                  </div>
                </div>


                <div className="flex items-start gap-4 bg-white p-4 rounded-xl">
                  <div className="bg-blue-600 p-3 rounded-lg flex-shrink-0">
                    <Mail className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">
                      {t('Email', 'Email')}
                    </h4>
                    <a 
                      href="mailto:cliniquemainsdoucesplus@gmail.com" 
                      className="text-blue-600 hover:text-blue-800 font-medium break-all"
                    >
                      cliniquemainsdoucesplus@gmail.com
                    </a>
                    <p className="text-sm text-gray-500 mt-1">
                      {t('We reply within 24 hours', 'Nous répondons sous 24h')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp Card */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-3xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <MessageCircle size={32} />
                <h3 className="text-xl font-bold">WhatsApp</h3>
              </div>
              <p className="mb-6 text-green-100">
                {t(
                  'For quick responses, contact us via WhatsApp. We typically respond within minutes.',
                  'Pour des réponses rapides, contactez-nous via WhatsApp. Nous répondons généralement en quelques minutes.'
                )}
              </p>
              <a
                href="https://wa.me/237653474614"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-white text-green-600 text-center px-6 py-4 rounded-xl font-semibold hover:bg-green-50 transition transform hover:scale-105"
              >
                {t('Chat on WhatsApp', 'Discuter sur WhatsApp')}
              </a>
            </div>
          </div>

          {/* Right Column - Appointment Form */}
          <div>
            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-blue-100">
              <h3 className="text-2xl font-bold text-blue-900 mb-6">
                {t('Book an Appointment', 'Prendre Rendez-vous')}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Full Name', 'Nom Complet')} *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder={t('John Doe', 'Jean Dupont')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Phone Number', 'Numéro de Téléphone')} *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="+237 6XX XXX XXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Email (Optional)', 'Email (Optionnel)')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="example@email.com"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('Preferred Date', 'Date Préférée')} *
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('Preferred Time', 'Heure Préférée')} *
                    </label>
                    <select
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    >
                      <option value="">{t('Select time', 'Choisir heure')}</option>
                      <option value="morning">{t('Morning (8:00 - 12:00)', 'Matin (8h - 12h)')}</option>
                      <option value="afternoon">{t('Afternoon (12:00 - 18:00)', 'Après-midi (12h - 18h)')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Reason for Visit', 'Motif de la Visite')}
                  </label>
                  <textarea
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder={t('Briefly describe your symptoms...', 'Décrivez brièvement vos symptômes...')}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-xl font-semibold text-lg transition transform hover:scale-105 shadow-lg"
                >
                  {t('Request Appointment', 'Demander Rendez-vous')}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-12 bg-white rounded-3xl shadow-xl p-6 overflow-hidden">
          <h3 className="text-xl font-bold text-blue-900 mb-4">
            {t('Find Us in Simbock', 'Trouvez-Nous à Simbock')}
          </h3>
          <div className="aspect-video bg-gray-200 rounded-2xl overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3989.6583697200855!2d11.495678!3d3.866667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM8KwNTEnMjAuMCJOIDExwrAyOSc0NC40IkU!5e0!3m2!1sen!2scm!4v1620000000000!5m2!1sen!2scm"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              title="Clinique Mains Douces Plus Location"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
}